<template>
  <div class="card-box">
    <div class="powered" v-if="logoLoginUrl">
      <img src="~assets/img/qreserve.svg" alt="Powered by QReserve" />
    </div>
    <div class="form-group">
      <!-- Logo, subtitle, and sign up text -->
      <div class="logo fade-in-load-logo">
        <img
          v-show="logoLoginUrl"
          :style="logoStyle"
          :src="logoLoginUrl"
          :alt="themeName" />
        <img
          v-show="!logoLoginUrl"
          src="~assets/img/qreserve.svg"
          alt="QReserve" />
      </div>

      <!-- Username and Password Entry -->
      <div class="fade-in-load-login">
        <div class="login-form">
          <h5
            v-if="!themeId"
            class="text-center title"
            style="margin: 0.6em 0 0.6em 0; font-size: 2.2rem">
            Create Your New Account
            <div
              style="font-weight: normal; font-size: 1.8rem; margin-top: 0.5em">
              Join an existing site or start your own by creating your new
              account below.
            </div>
          </h5>
          <h5
            v-if="themeId"
            class="text-center title"
            style="margin: 0.6em 0 0.6em 0; font-size: 1.8rem">
            We're so glad you're here!
            <br />
            Create your account below to get started.
          </h5>

          <button
            v-if="!QReserveStandaloneInstall"
            class="btn btn-link above-input-message"
            @click="goToLogin()">
            Return to sign in page
          </button>

          <div
            style="margin-top: 1em"
            class="form-group"
            :class="{
              'has-error': loginInfoEntered && !validEmail,
              valid: validEmail && credentials.email && !userAlreadyRegistered,
            }">
            <label class="form-label" for="email-address">E-mail address</label>
            <div class="has-icon-left">
              <span class="icon form-icon">
                <font-awesome-icon
                  :icon="faIcons.faCheckCircleSolid"
                  v-if="validEmail && credentials.email" />
                <font-awesome-icon v-else :icon="faIcons.faEnvelope" />
              </span>
              <input
                class="form-input form-icon-offset"
                type="email"
                id="signup-email-address"
                v-model="credentials.email"
                :disabled="Boolean(emailOverride) || null"
                @keydown.enter="doSignup()"
                v-focus />
            </div>
            <transition name="fade">
              <p
                class="form-input-hint"
                v-show="
                  !(loginInfoEntered && !validEmail) && !entityIdForEmailDomain
                "
                style="color: #47525d">
                We'll send you a quick email to verify your address.
              </p>
            </transition>

            <transition name="fade">
              <div v-show="loginInfoEntered && !validEmail">
                <p aria-live="assertive" class="form-input-hint">
                  This is not a valid email address.
                </p>
              </div>
            </transition>
          </div>

          <div
            class="form-group"
            v-if="!entityIdForEmailDomain"
            :class="{
              'has-error':
                (loginInfoEntered && !validPassword) ||
                error ||
                localSignupNotPermitted,
              valid: loginInfoEntered && validPassword && !error,
            }"
            style="margin-top: 2em">
            <label class="form-label" for="password">Choose a password</label>
            <span class="icon form-icon">
              <font-awesome-icon
                :icon="faIcons.faLockSolid"
                v-if="loginInfoEntered && validPassword && !error" />
              <font-awesome-icon v-else :icon="faIcons.faLock" />
            </span>
            <input
              class="form-input form-icon-offset"
              maxlength="8192"
              type="password"
              id="signup-password"
              v-model="credentials.password" />

            <label
              class="form-label"
              for="confirm-password"
              style="margin-top: 0.5em">
              Verify your password is correct
            </label>
            <span class="icon form-icon">
              <font-awesome-icon
                :icon="faIcons.faLockSolid"
                v-if="loginInfoEntered && validPassword && !error" />
              <font-awesome-icon v-else :icon="faIcons.faLock" />
            </span>
            <input
              class="form-input form-icon-offset"
              type="password"
              id="signup-confirm-password"
              maxlength="8192"
              v-model="credentials.confirmPassword"
              @keydown.enter="doSignup()" />
            <transition name="fade">
              <div
                v-show="
                  (loginInfoEntered && !validPassword) || userAlreadyRegistered
                ">
                <p aria-live="assertive" class="form-input-hint">
                  {{ passwordFailMessage }}
                </p>
              </div>
            </transition>
            <transition name="fade">
              <div v-show="error && !passwordFailMessage">
                <p aria-live="assertive" class="form-input-hint">
                  Error creating account. Please contact support.
                </p>
              </div>
            </transition>
            <transition name="fade">
              <div v-show="localSignupNotPermitted">
                <p aria-live="assertive" class="form-input-hint">
                  You must register directly through your organization and not
                  on QReserve.
                </p>
              </div>
            </transition>
          </div>
          <div v-else>
            <button
              class="btn btn-primary card-button"
              id="sign-in"
              :class="{ loading: isLoading }"
              @click="
                $emit('emitLoginWithDefaultSSO', {
                  entity_id: entityIdForEmailDomain,
                })
              "
              :disabled="isLoading || !validEmail || null">
              Sign Up At {{ credentials.email.split('@').pop() }}
            </button>
          </div>
        </div>
        <div class="terms-box">
          By creating your account you agree to our
          <br />
          <a href="https://get.qreserve.com/legal/terms/">Terms of use</a>
          and
          <a href="https://get.qreserve.com/legal/privacy/">Privacy policy</a>
          .
        </div>
        <!-- Signup button -->
        <div class="button-box" v-if="!entityIdForEmailDomain">
          <button
            class="btn btn-primary card-button"
            id="sign-up"
            :class="{ loading: isLoading }"
            @click="doSignup()"
            :disabled="!validSignUp || null">
            Create my new account
            <template v-if="themeName">
              at
              <strong>{{ themeName }}</strong>
            </template>
          </button>
          <div v-if="themeName" class="theme-banner">
            <button class="btn btn-link inline" @click="$emit('clearTheme')">
              <strong>Wrong location?</strong>
              Return to QReserve
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';

import auth from 'assets/js/auth';
import { complexEnough, pw } from 'assets/js/passwords';

import Mixin from './Mixins';

const ERROR_API_ALREADY_IN_USE = 'Email address already registered';

const ERROR_MESSAGES = {
  missing: 'Must provide a password.',
  tooShort: 'Password must be at least 8 characters long',
  common: 'Password may not be a commonly used password.',
  invalidMatch: 'Passwords do not match.',
  alreadyRegistered: 'Email is already registered with QReserve.',
  complexity:
    'Passwords under 12 characters must contain of characters ' +
    'from lowercase, uppercase, numbers, and special characters.',
};

export default {
  name: 'signup-card',

  mixins: [Mixin],

  created() {
    this.setTitle('Create Your New Account');
    if (this.themeName) {
      this.setTitle(`Create Account for ${this.themeName}`);
    }
  },

  computed: {
    // loginInfoEntered checks to see if the user has entered something in
    // both the email and password fields.
    loginInfoEntered() {
      return this.credentials.email !== '' && this.credentials.password !== '';
    },

    // passwordFailMessage selects a message to display to the user if an
    // invalid password has been selected.
    passwordFailMessage() {
      const pwd = this.credentials.password;
      const confirmPwd = this.credentials.confirmPassword;
      if (!pwd) return ERROR_MESSAGES.missing;
      if (pwd.length < 8) return ERROR_MESSAGES.tooShort;
      if (this.commonPWObject[pwd]) return ERROR_MESSAGES.common;
      if (this.passwordNotComplex) return ERROR_MESSAGES.complexity;
      if (pwd !== confirmPwd) return ERROR_MESSAGES.invalidMatch;
      if (this.userAlreadyRegistered) {
        return ERROR_MESSAGES.alreadyRegistered;
      }

      return null;
    },

    passwordNotComplex() {
      return !complexEnough(this.credentials.password);
    },

    // validEmail checks the email string to see if it formatted like an
    // email address.
    validEmail() {
      if (this.emailOverride) return true;
      if (_.isUndefined(this.credentials.email)) {
        return true;
      }
      if (this.credentials.email === '') {
        return true;
      }
      return this.emailRegex().test(this.credentials.email);
    },

    // validPassword does a client-side check to ensure that the user isn't
    // going to pass an invalid password to the API.
    validPassword() {
      // Only do the validation in one place.
      return !this.passwordFailMessage;
    },

    validSignUp() {
      return (
        !this.isLoading &&
        this.loginInfoEntered &&
        this.validEmail &&
        this.validPassword &&
        !this.userAlreadyRegistered
      );
    },
  },

  data() {
    return {
      credentials: {
        email: '',
        password: '',
      },
      commonPWObject: this.arrayToObject(
        pw.PASSWORDS,
        (k) => k,
        () => true,
      ),
      error: null,
      localSignupNotPermitted: false,
      isLoading: false,
      userAlreadyRegistered: false,
    };
  },

  emits: ['clearTheme', 'done', 'emitLoginWithDefaultSSO'],

  methods: {
    // doSignup connects to the server by the API and attempts to create an
    // account with the provided credentials. If the signup is successful, it
    // logs in with a special one time use token.
    async doSignup() {
      if (!this.validSignUp) return;
      this.isLoading = true;

      const credentials = {
        email: this.credentials.email,
        password: this.credentials.password,
      };

      try {
        await auth.Signup(credentials, this.themeId);
        this.isLoading = false;
        this.$emit('done');
      } catch (err) {
        this.isLoading = false;

        // We registered a new account, so forward us to the login screen
        // and pop up a modal.
        const errorMessage = err.response?.data?.errors?.[0] || '';
        if (err.name === auth.ProductionTokenErrorName) {
          this.$emit('done');
          this.goToLogin(true);
        } else if (errorMessage?.indexOf('host organization') !== -1) {
          this.localSignupNotPermitted = true;
        } else {
          this.error = err;
        }
      }
    },
  },

  mounted() {
    if (this.emailOverride) this.credentials.email = this.emailOverride;
  },

  props: {
    // Functions
    goToLogin: {
      type: Function,
      required: true,
    },

    // Strings
    emailOverride: {
      type: String,
    },
    logoLoginUrl: {
      type: String,
      required: false,
    },
    logoStyle: {
      type: Object,
      required: false,
      default: () => {},
    },
    themeId: {
      required: false,
    },
    themeName: {
      type: String,
      required: false,
    },

    // Objects
  },

  watch: {
    'credentials.email': {
      handler: function handle() {
        this.userAlreadyRegistered = false;
      },
    },
    emailOverride: {
      handler: async function handle(eo) {
        if (eo) {
          this.credentials.email = eo;
        }
      },
    },
    error: {
      handler: async function handle(error) {
        if (error) {
          if (
            !_.includes(
              _.get(this.error, 'response.data.errors[0]'),
              ERROR_API_ALREADY_IN_USE,
            )
          ) {
            await this.sleep(4000);
          } else {
            this.userAlreadyRegistered = true;
          }
          this.error = null;
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~assets/css/colors';
@import '~assets/css/partials/login';

.powered {
  position: absolute;
  top: -4px;
  right: 8px;
  img {
    height: 8px;
  }
}

span.form-icon {
  position: absolute;
  margin-top: 6px;
  margin-left: 8px;
  z-index: 10;
  opacity: 0.4;
}

.form-icon-offset {
  padding: 12px 28px;
}

.valid {
  input {
    border-color: $successcolor;
    color: $successcolor;
    background-color: rgb(240, 255, 240);
  }
  span.form-icon {
    opacity: 1;
    svg {
      color: $successcolor;
    }
  }
}

.card-box .terms-box {
  font-size: 1.2rem;
  margin: 3.7em auto -0.5em auto;
  a {
    font-weight: bold;
    text-decoration: underline;
  }
}
.theme-banner {
  padding: 0.2em;
  margin-top: 1.2em;
  font-size: smaller;
  margin-left: -20px;
  margin-right: -20px;
  text-align: center;

  .btn-link {
    padding: 0;
    display: inline;
    height: 1.2em;
    line-height: 1.2em;
  }
}
</style>
